"use client";

import { Trans } from "@lingui/react/macro";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { RegisteredProperty } from "./iw-form-dialog";

interface PropertyComboboxProps {
  // GraphQLから導出した型を使用
  properties: ReadonlyArray<RegisteredProperty>;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  required?: boolean;
  id?: string;
  name?: string;
}

export function PropertyCombobox({
  properties,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  required,
  id,
  name,
}: PropertyComboboxProps) {
  const [open, setOpen] = React.useState(false);

  // プロパティからメールアドレスを取得するヘルパー関数
  const getPropertyEmail = React.useCallback(
    (property: RegisteredProperty): string => {
      if (
        property.__typename === "RegisteredSearchConsoleUserProperty" &&
        property.user
      ) {
        return property.user.email;
      } else if (
        property.__typename ===
          "RegisteredSearchConsoleServiceAccountProperty" &&
        property.serviceAccount
      ) {
        return property.serviceAccount.email;
      }
      return "";
    },
    [],
  );

  // 有効なプロパティのみをフィルタリング
  const validProperties = React.useMemo(() => {
    return properties.filter((p) => {
      // Search Consoleプロパティのみを対象とする
      return (
        p.__typename === "RegisteredSearchConsoleUserProperty" ||
        p.__typename === "RegisteredSearchConsoleServiceAccountProperty"
      );
    });
  }, [properties]);

  const selectedProperty = validProperties.find((p) => p.id === value);

  // cmdkのfilter関数を使用してフィルタリング
  const customFilter = React.useCallback(
    (itemValue: string, search: string) => {
      // itemValueは "property.property email" の形式
      const searchLower = search.toLowerCase();
      const valueLower = itemValue.toLowerCase();

      // 部分一致でスコアを計算
      if (valueLower.includes(searchLower)) {
        // 先頭一致の場合はより高いスコア
        if (valueLower.startsWith(searchLower)) {
          return 2;
        }
        return 1;
      }
      return 0;
    },
    [],
  );

  return (
    <>
      {name && (
        <input type="hidden" name={name} value={value} required={required} />
      )}

      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className="truncate">
              {selectedProperty ? (
                <div className="flex flex-row gap-4 items-center">
                  <span className="truncate">{selectedProperty.property}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {getPropertyEmail(selectedProperty)}
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command filter={customFilter}>
            <CommandInput placeholder={searchPlaceholder} className="h-9" />
            <CommandList className="max-h-[350px] overflow-hidden">
              <CommandEmpty>
                <Trans>No property found.</Trans>
              </CommandEmpty>
              <ScrollArea className="max-h-[300px]">
                <CommandGroup>
                  {validProperties.map((property) => {
                    const email = getPropertyEmail(property);
                    return (
                      <CommandItem
                        key={property.id}
                        value={`${property.property} ${email}`}
                        onSelect={() => {
                          onValueChange(property.id);
                          setOpen(false);
                        }}
                      >
                        <div className="flex flex-col flex-1">
                          <span>{property.property}</span>
                          {email && (
                            <span className="text-xs text-muted-foreground">
                              {email}
                            </span>
                          )}
                        </div>
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4",
                            value === property.id ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}
